import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

/** ─── Падежи ──────────────────────────────────────────────────────────── */

export interface DueItem {
  id: string;
  direction: 'receivable' | 'payable';
  counterparty: string;
  documentNo: string | null;
  category: string | null;
  dueDate: string;
  daysLeft: number;
  amountMinor: number;
  paidMinor: number;
  openMinor: number;
  ccy: string;
  status: string;
  vehicle: string | null;
  notes: string | null;
}

export interface ExpiryItem {
  id: string;
  kind: string;
  label: string;
  subject: string;
  reference: string | null;
  validUntil: string;
  daysLeft: number;
}

export interface DuesOverview {
  today: string;
  receivables: DueItem[];
  payables: DueItem[];
  expiries: ExpiryItem[];
  totals: {
    receivableOverdueMinor: number;
    receivableNext7Minor: number;
    receivableOpenMinor: number;
    payableOverdueMinor: number;
    payableNext7Minor: number;
    payableOpenMinor: number;
    expiriesOverdue: number;
    expiriesNext30: number;
  };
}

export interface PayableInput {
  supplierName: string;
  documentNo?: string;
  category: string;
  issueDate?: string;
  dueDate: string;
  amountMinor: number;
  ccy: string;
  vehicleId?: string;
  notes?: string;
}

export function useDues(days: number) {
  return useQuery({
    queryKey: ['dues', days],
    queryFn: () => api.get<DuesOverview>('/dues', { days }),
    staleTime: 60_000,
  });
}

export function useSavePayable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: PayableInput & { id?: string }) =>
      id ? api.post<{ id: string }>(`/payables/${id}`, body) : api.post<{ id: string }>('/payables', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dues'] }),
  });
}

export function usePayPayable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ id: string; status: string }>(`/payables/${id}/pay`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dues'] }),
  });
}

export function useDeletePayable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/payables/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dues'] }),
  });
}

/** ─── Гориво ──────────────────────────────────────────────────────────── */

export interface FuelVehicleRow {
  vehicleId: string;
  fleetNo: string;
  plate: string;
  fills: number;
  litres: number;
  costEurMinor: number;
  otherCurrencyFills: number;
  km: number | null;
  kmSource: 'odometer' | 'telematics' | 'gps' | null;
  litresPer100: number | null;
  costPerKmMinor: number | null;
  telematicsKm: number | null;
  telematicsFuelL: number | null;
  telematicsLitresPer100: number | null;
  lastFillAt: string | null;
  anomalies: number;
}

export interface FuelReport {
  from: string;
  to: string;
  vehicles: FuelVehicleRow[];
  totals: { litres: number; costEurMinor: number; km: number; litresPer100: number | null; costPerKmMinor: number | null };
}

export interface FuelTx {
  id: string;
  vehicleId: string | null;
  vehicle: string | null;
  transactedAt: string;
  station: string | null;
  country: string | null;
  litres: number | null;
  grossMinor: number;
  ccy: string;
  odometerKm: number | null;
  provider: string;
  anomalyFlags: string[];
}

export function useFuelReport(from: string, to: string) {
  return useQuery({
    queryKey: ['fuel', 'report', from, to],
    queryFn: () => api.get<FuelReport>('/expenses/fuel/report', { from, to }),
    staleTime: 60_000,
  });
}

export function useFuelTransactions(from: string, to: string, vehicleId?: string) {
  return useQuery({
    queryKey: ['fuel', 'tx', from, to, vehicleId ?? 'all'],
    queryFn: () => api.get<FuelTx[]>('/expenses/fuel/transactions', { from, to, vehicleId }),
    staleTime: 60_000,
  });
}

export function useAddFuel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { vehicleId: string; transactedAt: string; litres: number; grossMinor: number; ccy: string; odometerKm?: number; station?: string; country?: string }) =>
      api.post<{ id: string }>('/expenses/fuel/transactions', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['fuel'] }),
  });
}

export function useImportFuel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { provider: string; content: string; createExpenses?: boolean }) =>
      api.post<{
        parsed: number;
        imported: number;
        duplicates: number;
        failed: { lineNo: number; reason: string }[];
        unmatchedCards: string[];
        anomalies: { lineNo: number; externalId: string; flags: string[]; note: string }[];
      }>('/expenses/fuel/import', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['fuel'] }),
  });
}

export function useFuelProviders() {
  return useQuery({
    queryKey: ['fuel', 'providers'],
    queryFn: () => api.get<{ providers: { key: string; label: string }[] }>('/expenses/fuel/providers'),
    staleTime: 60 * 60_000,
  });
}

/** ─── Горивни карти (AS 24 и др.) ──────────────────────────────────────── */

export interface FuelCard {
  id: string;
  provider: string;
  cardNumber: string;
  label: string | null;
  vehicleId: string | null;
  vehicle: string | null;
  driverId: string | null;
  driverName: string | null;
  isActive: boolean;
  validUntil: string | null;
  notes: string | null;
}

export function useFuelCards(includeInactive = false) {
  return useQuery({
    queryKey: ['fuel', 'cards', includeInactive],
    queryFn: () => api.get<FuelCard[]>('/fuel-cards', includeInactive ? { includeInactive: true } : {}),
  });
}

export interface FuelCardInput {
  provider: string;
  cardNumber: string;
  label?: string | null;
  vehicleId?: string | null;
  driverId?: string | null;
  isActive?: boolean;
  validUntil?: string | null;
  notes?: string | null;
}

export function useSaveFuelCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id?: string; body: FuelCardInput }) =>
      input.id ? api.patch<FuelCard>(`/fuel-cards/${input.id}`, input.body) : api.post<FuelCard>('/fuel-cards', input.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fuel'] });
      void qc.invalidateQueries({ queryKey: ['anomalies'] });
    },
  });
}
